import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

export class HeaderComponent extends Component {
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initScroll();
    this.#initHeroTone();
    // The chrome island is morphed from the incoming document on every soft
    // navigation (server-rendered per request.page_type), so the header's
    // page-dependent mode arrives with the DOM. This handler re-derives the
    // continuous scroll/hero behaviour from that committed state — it runs
    // inside the view-transition update callback (site:navigation:commit),
    // i.e. before the new state is captured, so the new page never paints
    // a stale header.
    document.addEventListener(
      ThemeEvents.navigationCommit,
      this.#onNavigationCommit
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    window.removeEventListener('scroll', this.#scrollSync);
    document.removeEventListener(
      ThemeEvents.navigationCommit,
      this.#onNavigationCommit
    );
  }

  #onNavigationCommit = () => {
    this.#initScroll();
    this.#syncHeroTone();
  };

  toggleMenuDrawer() {
    const drawer = document.querySelector('header-drawer-component');
    if (!drawer) return;
    const dialog = drawer.querySelector('dialog');
    if (dialog && dialog.open) drawer.closeDialog();
    else drawer.showDialog();
  }

  toggleCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    drawer.toggle();
  }

  #scrollSync = () => this.toggleAttribute('data-scrolled', window.scrollY > 16);

  #initScroll() {
    // Server truth: the incoming document rendered `data-header-mode` for
    // the target page (the transparent_pages setting and all); the chrome
    // morph delivers it here untouched, so no pathname guessing in JS.
    const isHomepage = this.dataset.headerMode === 'homepage';
    this.classList.toggle('is-homepage', isHomepage);
    const setScrolled = (v) => this.toggleAttribute('data-scrolled', v);

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    window.removeEventListener('scroll', this.#scrollSync);

    if (!isHomepage) {
      setScrolled(true);
      return;
    }

    const hero = document.querySelector('[data-hero-root]');
    if (hero && 'IntersectionObserver' in window) {
      let sentinel = hero.querySelector('[data-header-hero-sentinel]');
      if (!sentinel) {
        sentinel = document.createElement('span');
        sentinel.setAttribute('data-header-hero-sentinel', '');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:absolute;inset:16px auto auto 0;width:1px;height:1px;pointer-events:none';
        hero.insertAdjacentElement('afterbegin', sentinel);
      }
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => setScrolled(!entries[0].isIntersecting),
        { threshold: 0 }
      );
      this.#intersectionObserver.observe(sentinel);
    } else {
      this.#scrollSync();
      window.addEventListener('scroll', this.#scrollSync, { passive: true });
    }
  }

  #initHeroTone() {
    this.#syncHeroTone();
    document.addEventListener(ThemeEvents.heroTone, this.#onHeroTone);
  }

  #syncHeroTone() {
    const hero = document.querySelector('[data-hero-root]');
    if (hero?.dataset?.heroTone) {
      this.dataset.heroTone = hero.dataset.heroTone;
    }
  }

  #onHeroTone = (event) => {
    if (event.detail?.tone) {
      this.dataset.heroTone = event.detail.tone;
    }
  };
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
